export type IntegrationStatus =
  | "SCHEDULED"
  | "RUNNING"
  | "SUCCEEDED"
  | "FAILED"
  | "CANCELLED"
  | "MISSING"
  | "LATE"
  | "LONG_RUNNING"
  | "UNKNOWN";

export type IntegrationJobStatus = IntegrationStatus;

export type Severity = "LOW" | "MEDIUM" | "HIGH" | "CRITICAL";

export type SourceType = "SYNAPSE" | "ACR" | "CUSTOM" | "AZURE_MONITOR" | "SQL_ETL" | "POWER_BI" | "MARKETING";

export const DEFAULT_JOB_OWNER = "Samuel Kim";

export type PortalAccessStatus = "PENDING" | "ACTIVE" | "SUSPENDED" | "DENIED" | "SETUP_REQUIRED";
export type PortalStationAccessRole = "VIEWER" | "MANAGER" | "OWNER";

export interface PortalStationAccess {
  stationSk: number;
  stationName?: string;
  businessUnitName?: string;
  province?: string;
  address?: string;
  latitude?: string;
  longitude?: string;
  accessRole: PortalStationAccessRole;
}

export interface PortalAccessAdminStation extends PortalStationAccess {
  isStation?: boolean;
}

export interface PortalAccessAdminAssignment extends PortalStationAccess {
  accessId: string;
  isActive: boolean;
  grantedAt?: string;
  revokedAt?: string;
}

export interface PortalAccessAdminUser {
  entraObjectId: string;
  userPrincipalName?: string;
  email?: string;
  displayName: string;
  userType?: string;
  accessStatus: Exclude<PortalAccessStatus, "SETUP_REQUIRED">;
  requestedAt?: string;
  approvedAt?: string;
  deniedAt?: string;
  suspendedAt?: string;
  lastSignedInAt?: string;
  createdAt?: string;
  updatedAt?: string;
  stations: PortalAccessAdminAssignment[];
}

export interface PortalAccessAdminSnapshot {
  users: PortalAccessAdminUser[];
  stations: PortalAccessAdminStation[];
}

export interface PortalAppRoleDefinition {
  id: string;
  value: string;
  displayName: string;
  description?: string;
  isEnabled: boolean;
}

export interface PortalAppRoleAssignment {
  id: string;
  principalId: string;
  principalDisplayName: string;
  principalType: "User" | "Group" | "ServicePrincipal" | string;
  appRoleId: string;
  roleValue?: string;
  roleDisplayName?: string;
  createdDateTime?: string;
}

export interface PortalRolePrincipal {
  id: string;
  displayName: string;
  principalType: "User" | "Group";
  userPrincipalName?: string;
  mail?: string;
}

export interface PortalRoleAdminSnapshot {
  roles: PortalAppRoleDefinition[];
  assignments: PortalAppRoleAssignment[];
}

export interface PortalRolePrincipalSearchResult {
  principals: PortalRolePrincipal[];
}

export type PortalUsageRange = "7d" | "30d" | "90d";

export interface PortalUsageSummary {
  range: PortalUsageRange;
  rangeStartUtc: string;
  rangeEndUtc: string;
  refreshedAtUtc?: string;
  cached?: boolean;
  successfulSignIns: number;
  distinctSignInUsers: number;
  apiRequests: number;
  failedApiRequests: number;
  pageViews: number;
  distinctTelemetryUsers: number;
  telemetryNotice?: string;
}

export interface PortalUsageUserRow {
  userPrincipalName: string;
  displayName: string;
  signIns: number;
  firstSignInUtc: string;
  lastSignInUtc: string;
  browsers: string[];
  operatingSystems: string[];
  locations: string[];
}

export interface PortalUsageDailyRow {
  date: string;
  successfulSignIns: number;
  distinctUsers: number;
  apiRequests: number;
  failedApiRequests: number;
  pageViews: number;
}

export interface PortalUsageEndpointRow {
  name: string;
  url?: string;
  requests: number;
  failed: number;
  avgDurationMs: number;
}

export interface PortalUsagePageRow {
  name: string;
  url?: string;
  views: number;
  users: number;
  sessions: number;
  avgDurationMs?: number;
}

export interface PortalUsageSnapshot {
  summary: PortalUsageSummary;
  users: PortalUsageUserRow[];
  daily: PortalUsageDailyRow[];
  endpoints: PortalUsageEndpointRow[];
  pages: PortalUsagePageRow[];
  warnings: string[];
}

export interface PortalRoleGrantRequest {
  principalId: string;
  appRoleId: string;
}

export interface PortalUserStatusUpdateRequest {
  accessStatus: Exclude<PortalAccessStatus, "SETUP_REQUIRED">;
}

export interface PortalStationGrantRequest {
  stationSk?: number;
  stationSks?: number[];
  accessRole: PortalStationAccessRole;
}

export interface PortalUserContext {
  entraObjectId?: string;
  displayName: string;
  userPrincipalName?: string;
  email?: string;
  userType?: string;
  roles: string[];
  accessStatus: PortalAccessStatus;
  stations: PortalStationAccess[];
  setupRequired?: boolean;
  features: {
    stationAnalytics: boolean;
    reports: boolean;
    profitLoss: boolean;
    businessAnalysis: boolean;
    marketingAnalytics: boolean;
    portalExecutiveMember: boolean;
    stationAccessAdmin: boolean;
    healthMonitoring: boolean;
    healthOperations: boolean;
    healthAdmin: boolean;
  };
}

export interface StationMetricValue {
  label: string;
  value: number;
  unit: "currency" | "number" | "percent" | "volume";
  delta?: number;
  deltaLabel?: string;
  helper?: string;
  comparison?: { current?: number; previous?: number; label: string; detail: string };
}

export interface StationSalesTrendPoint {
  period: string;
  cstoreSales: number;
  fuelSales: number;
  grossProfit: number;
  fuelVolumeLitres: number;
}

export interface StationPerformanceRow {
  stationSk: number;
  stationName: string;
  province: string;
  cstoreSales: number;
  fuelSales: number;
  grossProfit: number;
  fuelVolumeLitres: number;
  transactions: number;
}

export interface StationCStoreCategoryRow {
  category: string;
  sales: number;
  profit: number;
  gpm: number;
  salesQty: number;
}

export interface StationCStoreTrendPoint {
  period: string;
  sales: number;
  profit: number;
  transactions: number;
}

export interface StationFuelGradeRow {
  grade: string;
  salesAmount: number;
  volumeLitres: number;
  avgRetailPrice: number;
  poolMargin: number;
}

export interface StationFuelTrendPoint {
  period: string;
  salesAmount: number;
  volumeLitres: number;
  avgRetailPrice: number;
}

export interface StationFuelGradePriceTrendPoint {
  date: string;
  period: string;
  grade: string;
  avgRetailPrice: number;
}

export interface StationWeeklySalesPoint {
  day: string;
  cstoreSales: number;
  fuelSales: number;
}

export interface StationDateRange {
  startDate: string;
  endDate: string;
}

export interface StationMtdSalesPoint {
  day: number;
  current: number;
  previous: number;
}

export interface StationAnalyticsSnapshot {
  delivery?: {
    snapshotId: string;
    sourceRefreshId: string;
    sourceRefreshedAt: string;
    publishedAt: string;
    status: "current" | "stale";
  };
  asOfDate: string;
  periodLabel: string;
  overview: {
    totalSalesYtd: StationMetricValue;
    cstoreSalesYtd: StationMetricValue;
    fuelSalesYtd: StationMetricValue;
    grossProfitYtd: StationMetricValue;
    fuelVolumeYtd: StationMetricValue;
    transactionsYtd: StationMetricValue;
  };
  monthlyTrend: StationSalesTrendPoint[];
  weeklySalesRange: StationDateRange;
  weeklySales: StationWeeklySalesPoint[];
  stationPerformance: StationPerformanceRow[];
  cstore: {
    metrics: {
      salesYtd: StationMetricValue;
      profitYtd: StationMetricValue;
      gpmYtd: StationMetricValue;
      transactionsYtd: StationMetricValue;
    };
    mtd: {
      sales: StationMetricValue;
      profit: StationMetricValue;
      gpm: StationMetricValue;
      transactions: StationMetricValue;
    };
    mtdTrend: StationMtdSalesPoint[];
    trend: StationCStoreTrendPoint[];
    categories: StationCStoreCategoryRow[];
  };
  fuel: {
    metrics: {
      salesYtd: StationMetricValue;
      volumeYtd: StationMetricValue;
      avgRetailPriceYtd: StationMetricValue;
      poolMarginYtd: StationMetricValue;
    };
    mtd: {
      sales: StationMetricValue;
      volume: StationMetricValue;
      price: StationMetricValue;
    };
    mtdTrend: StationMtdSalesPoint[];
    trend: StationFuelTrendPoint[];
    grades: StationFuelGradeRow[];
    gradePriceTrend: StationFuelGradePriceTrendPoint[];
  };
}

export interface MarketingMetricValue {
  label: string;
  value: number;
  unit: "number" | "percent" | "seconds";
  helper?: string;
}

export type MarketingTrendPeriodKey = "day" | "wtd" | "mtd" | "mom";

export type MarketingTrendMetricKey =
  | "activeUsers"
  | "eventCount"
  | "newUsers"
  | "sessions"
  | "pageViews"
  | "clicks"
  | "keyEvents"
  | "socialFollowers"
  | "socialViews"
  | "socialEngagements"
  | "engagementSecondsPerSession";

export interface MarketingTrendComparison {
  current: number;
  previous: number;
  change?: number;
}

export type MarketingMetricTrendSet = Partial<Record<MarketingTrendPeriodKey, MarketingTrendComparison>>;

export type MarketingTrendSummary = Partial<Record<MarketingTrendMetricKey, MarketingMetricTrendSet>>;

export type MarketingSocialPlatformMetricKey = "engagements" | "followers" | "reach" | "views";

export type MarketingSocialPlatformTrendSummary = Record<string, Partial<Record<MarketingSocialPlatformMetricKey, MarketingMetricTrendSet>>>;

export interface MarketingDailyTrendPoint {
  date: string;
  sessions: number;
  activeUsers: number;
  newUsers: number;
  pageViews: number;
  eventCount: number;
  clicks: number;
  keyEvents: number;
  engagementSeconds: number;
}

export interface MarketingHourlyTrafficPoint {
  hour: number;
  hourLabel: string;
  daypart: string;
  sessions: number;
  activeUsers: number;
  pageViews: number;
  clicks: number;
  clicksPerSession: number;
  engagementSeconds: number;
}

export interface MarketingChannelRow {
  channelGroup: string;
  sourceMedium: string;
  sessions: number;
  activeUsers: number;
  pageViews: number;
  events: number;
  keyEvents: number;
  engagementSecondsPerSession: number;
}

export interface MarketingPageRow {
  pagePath: string;
  pageTitle: string;
  sessions: number;
  activeUsers: number;
  pageViews: number;
  keyEvents: number;
  engagementSecondsPerSession: number;
}

export interface MarketingGeoRow {
  country: string;
  region: string;
  city: string;
  sessions: number;
  activeUsers: number;
  pageViews: number;
  keyEvents: number;
}

export interface MarketingAudienceRow {
  segment: string;
  platform?: string;
  ageBracket?: string;
  gender?: string;
  country?: string;
  city?: string;
  activeUsers: number;
  totalUsers: number;
  sessions: number;
  pageViews: number;
  followers?: number;
}

export interface MarketingSocialPlatformRow {
  platform: string;
  followers: number;
  reach: number;
  profileViews: number;
  views: number;
  videoViews: number;
  accountEngagements: number;
  contentEngagements: number;
  engagements: number;
}

export interface MarketingSocialAccountRow {
  platform: string;
  sourceName: string;
  followers: number;
  reach: number;
  profileViews: number;
  views: number;
  postEngagements: number;
  videoViews: number;
}

export interface MarketingSocialAccountTrendRow {
  platform: string;
  sourceName: string;
  date: string;
  followers: number;
  reach: number;
  views: number;
  engagements: number;
}

export interface MarketingContentRow {
  platform: string;
  sourceName: string;
  contentType: string;
  caption: string;
  publishedAt?: string;
  reach: number | null;
  views: number | null;
  likes: number;
  comments: number;
  shares: number;
  saves: number;
  clicks: number;
  engagementTotal: number;
  engagementRate: number | null;
  permalinkUrl?: string;
  mediaUrl?: string;
  thumbnailUrl?: string;
  thumbnailKey?: string;
}

export interface MarketingPipelineStatusRow {
  pipelineName: string;
  platform?: string;
  sourceName?: string;
  status: string;
  triggerType: string;
  windowStartUtc?: string;
  windowEndUtc?: string;
  rowsLoadedCount: number;
  errorMessage?: string;
}

export interface MarketingAnalyticsSnapshot {
  delivery?: { snapshotId: string; publishedAt: string; sourceRefreshedAt: string; status: "current" | "stale" };
  asOfDate: string;
  periodLabel: string;
  trendSummary?: MarketingTrendSummary;
  web: {
    sessions: MarketingMetricValue;
    activeUsers: MarketingMetricValue;
    newUsers: MarketingMetricValue;
    pageViews: MarketingMetricValue;
    eventCount: MarketingMetricValue;
    clicks: MarketingMetricValue;
    keyEvents: MarketingMetricValue;
    engagementSecondsPerSession: MarketingMetricValue;
    bounceRate: MarketingMetricValue;
  };
  social: {
    followers: MarketingMetricValue;
    reach: MarketingMetricValue;
    profileViews: MarketingMetricValue;
    views: MarketingMetricValue;
    engagements: MarketingMetricValue;
  };
  dailyTrend: MarketingDailyTrendPoint[];
  hourlyTraffic: MarketingHourlyTrafficPoint[];
  channels: MarketingChannelRow[];
  pages: MarketingPageRow[];
  geography: MarketingGeoRow[];
  ga4Audience: MarketingAudienceRow[];
  socialAudience: MarketingAudienceRow[];
  socialPlatforms: MarketingSocialPlatformRow[];
  socialPlatformTrendSummary?: MarketingSocialPlatformTrendSummary;
  socialAccounts: MarketingSocialAccountRow[];
  socialAccountTrend: MarketingSocialAccountTrendRow[];
  content: MarketingContentRow[];
  pipelineStatus: MarketingPipelineStatusRow[];
}

export type ProfitLossMetricUnit = "currency" | "number" | "percent";

export interface ProfitLossMetricValue {
  label: string;
  value: number;
  unit: ProfitLossMetricUnit;
  delta?: number;
  deltaLabel?: string;
  helper?: string;
}

export type ProfitLossSankeyTone = "revenue" | "cost" | "expense" | "profit" | "tax" | "neutral";

export interface ProfitLossSankeyNode {
  id: string;
  label: string;
  value: number;
  level: number;
  tone: ProfitLossSankeyTone;
  delta?: number;
  helper?: string;
  order?: number;
}

export interface ProfitLossSankeyLink {
  source: string;
  target: string;
  value: number;
  tone: ProfitLossSankeyTone;
  label?: string;
}

export interface ProfitLossBreakdownRow {
  label: string;
  group: "revenue" | "cost" | "expense" | "tax" | "profit";
  value: number;
  percentOfRevenue: number;
  delta?: number;
}

export interface ProfitLossSnapshot {
  asOfDate: string;
  periodLabel: string;
  currency: string;
  overview: {
    revenue: ProfitLossMetricValue;
    grossProfit: ProfitLossMetricValue;
    operatingProfit: ProfitLossMetricValue;
    netProfit: ProfitLossMetricValue;
    operatingExpenses: ProfitLossMetricValue;
  };
  sankey: {
    nodes: ProfitLossSankeyNode[];
    links: ProfitLossSankeyLink[];
  };
  breakdown: ProfitLossBreakdownRow[];
}

export type BistroMovement = "new" | "surging" | "growing" | "steady" | "declining" | "inactive";

export interface BistroMetric {
  current: number;
  previous: number;
  growthRate: number | null;
}

export interface BistroProductPerformance {
  station: string;
  stationSk: string;
  category: string;
  subCategory: string;
  upc: string;
  itemName: string;
  sales: BistroMetric;
  units: BistroMetric;
  transactions: BistroMetric;
  movement: BistroMovement;
}

export interface BistroCategoryPerformance {
  category: string;
  subCategory?: string;
  sales: BistroMetric;
  units: BistroMetric;
  share: number;
  bestSeller?: BistroProductPerformance;
  slowSeller?: BistroProductPerformance;
}

export interface BistroStationPerformance {
  station: string;
  stationSk: string;
  sales: BistroMetric;
  units: BistroMetric;
  bestSeller?: BistroProductPerformance;
  slowSeller?: BistroProductPerformance;
}

export interface BistroSnapshot {
  asOfDate: string;
  currency: string;
  periods: {
    current: { start: string; end: string; label: string };
    previous: { start: string; end: string; label: string };
  };
  filters: {
    available: {
      stations: Array<{ value: string; label: string }>;
      categories: string[];
      bistroCategories: string[];
      subCategories: string[];
    };
    selected: { station?: string; category?: string; bistroCategory?: string; subCategory?: string };
  };
  overview: {
    sales: BistroMetric;
    units: BistroMetric;
    transactions: BistroMetric;
    averageSale: BistroMetric;
  };
  dailyTrend: Array<{ date: string; sales: number; units: number; period: "current" | "previous" }>;
  categories: BistroCategoryPerformance[];
  subCategories: BistroCategoryPerformance[];
  products: BistroProductPerformance[];
  stations: BistroStationPerformance[];
  insights: string[];
}

export interface OrderPlanningOverview {
  orderCount: number;
  lineCount: number;
  itemCount: number;
  orderQuantity: number;
  orderedCost: number;
  estimatedRetail: number;
  potentialMargin: number;
  salesAmount: number;
  salesQuantity: number;
  periodStartOnHandQty: number;
  currentInventory: number;
  daysOnHand: number;
  inventoryTurnover: number;
}

export interface PurchaseOrderSummaryRow {
  orderReference: string;
  station: string;
  stationSk: string;
  vendor: string;
  invoiceId: string;
  invoicedDate: string;
  invoiceType: string;
  type: string;
  acceptedDate: string;
  itemCount: number;
  orderQuantity: number;
  orderedCost: number;
  estimatedRetail: number;
  periodStartOnHandQty: number;
  currentOnHandQty: number;
}

export interface PurchaseOrderCategoryRow {
  categoryGroup1: string;
  categoryGroup2: string;
  categoryName: string;
  orderQuantity: number;
  orderedCost: number;
  estimatedRetail: number;
  salesAmount: number;
  salesQuantity: number;
  periodStartOnHandQty: number;
  currentOnHandQty: number;
  daysOnHand: number;
}

export interface PurchaseOrderStationComparisonRow {
  station: string;
  stationSk: string;
  orderCount: number;
  itemCount: number;
  orderQuantity: number;
  orderedCost: number;
  estimatedRetail: number;
  salesAmount: number;
  salesQuantity: number;
  periodStartOnHandQty: number;
  currentOnHandQty: number;
  daysOnHand: number;
}

export interface PurchaseOrderItemRow {
  orderReference: string;
  station: string;
  stationSk: string;
  vendor: string;
  invoiceId: string;
  invoicedDate: string;
  categoryGroup2: string;
  categoryName: string;
  upc: string;
  itemName: string;
  uom: string;
  caseQuantity: number;
  unitsPerCase: number;
  orderQuantity: number;
  orderedCost: number;
  estimatedRetail: number;
  salesQuantity: number;
  salesAmount: number;
  periodStartOnHandQty: number;
  currentOnHandQty: number;
  daysOnHand: number;
  inventoryTurnover: number;
}

export interface ReportSnapshotDelivery {
  status: "current" | "stale";
  generatedAt: string;
  expiresAt: string;
  source: "memory" | "storage" | "powerbi";
  refreshQueued?: boolean;
}

export interface OrderPlanningSnapshot {
  delivery?: ReportSnapshotDelivery;
  asOfDate: string;
  filters: {
    available: { timeFrames: string[]; stations: string[]; vendors: string[]; types: string[] };
    selected: { timeFrame: string; station?: string; vendor?: string; type?: string };
  };
  overview: OrderPlanningOverview;
  orders: PurchaseOrderSummaryRow[];
  categories: PurchaseOrderCategoryRow[];
  stationComparison: PurchaseOrderStationComparisonRow[];
  items: PurchaseOrderItemRow[];
}

export interface InvoiceAnalysisFilters {
  dateFrom: string;
  dateTo: string;
  station?: string;
  vendor?: string;
  categoryGroup2?: string;
  invoiceId?: string;
}

export type InvoiceHeaderRow = Omit<PurchaseOrderSummaryRow, "periodStartOnHandQty" | "currentOnHandQty">;
export type InvoiceItemRow = Omit<PurchaseOrderItemRow, "salesQuantity" | "salesAmount" | "periodStartOnHandQty" | "currentOnHandQty" | "daysOnHand" | "inventoryTurnover">;

export interface InvoiceAnalysisSnapshot {
  delivery?: ReportSnapshotDelivery;
  asOfDate: string;
  filters: {
    available: { stations: string[]; vendors: string[]; categories: string[]; invoiceIds: string[] };
    selected: InvoiceAnalysisFilters;
  };
  orders: InvoiceHeaderRow[];
  items: InvoiceItemRow[];
}

export type BusinessAnalysisRadiusKey = "R05" | "R10" | "R20";

export interface BusinessAnalysisScore {
  key:
    | "growth"
    | "residentialMarket"
    | "daytimeMarket"
    | "ageProfile"
    | "food"
    | "snacksBeverage"
    | "tobacco"
    | "cigarettes"
    | "cannabis"
    | "fuel";
  label: string;
  score: number;
  benchmarkScore: number;
  weight: number;
  value: number;
  benchmarkValue: number;
  unit: "number" | "currency" | "percent";
  description: string;
}

export interface BusinessAnalysisCategoryPotential {
  category: "Food" | "Snacks & Beverage" | "Tobacco" | "Cigarettes" | "Cannabis" | "Fuel";
  aoiExpenditure: number;
  benchmarkExpenditure: number;
  index: number;
}

export interface BusinessAnalysisRadiusResult {
  radiusKey: BusinessAnalysisRadiusKey;
  radiusMinutes: number;
  suitabilityScore: number;
  opportunityScore: number;
  stationFitScore: number;
  grade: "Excellent" | "Strong" | "Moderate" | "Caution";
  comparableStation: {
    poiSk?: string;
    name: string;
    city?: string;
    province?: string;
    address?: string;
    similarityScore: number;
  };
  scores: BusinessAnalysisScore[];
  categories: BusinessAnalysisCategoryPotential[];
  highlights: string[];
  risks: string[];
}

export interface BusinessAnalysisSalesBenchmark {
  stationSk: string;
  stationName: string;
  category: string;
  actualSales: number;
  esriExpenditure: number;
  realizationRate: number;
}

export interface BusinessAnalysisAoi {
  aoiSk: string;
  name: string;
  city?: string;
  province?: string;
  address?: string;
  overallScore: number;
  recommendation: "Prioritize" | "Advance with validation" | "Monitor" | "Do not prioritize";
  results: BusinessAnalysisRadiusResult[];
}

export interface BusinessAnalysisSnapshot {
  asOfDate: string;
  methodologyVersion: string;
  aoiCount: number;
  stationCount: number;
  aois: BusinessAnalysisAoi[];
  salesBenchmarks: BusinessAnalysisSalesBenchmark[];
}

export interface IntegrationJob {
  id: string;
  name: string;
  description: string;
  sourceType: SourceType;
  sourceName: string;
  expectedStartTime: string;
  cronExpression: string;
  graceMinutes: number;
  expectedDurationMinutes: number;
  status: IntegrationJobStatus;
  severity: Severity;
  owner: string;
  isActive: boolean;
  rawPayloadPath?: string;
  workspaceName?: string;
  pipelineName?: string;
  triggerName?: string;
  statusLastCheckedAt?: string;
  statusReason?: string;
  createdAt: string;
  updatedAt: string;
}

export interface IntegrationRun {
  id: string;
  jobId: string;
  jobName: string;
  sourceType: SourceType;
  sourceName: string;
  expectedStartTime: string;
  actualStartTime?: string;
  endTime?: string;
  durationMinutes?: number;
  status: IntegrationStatus;
  severity: Severity;
  lastError?: string;
  owner: string;
  rawPayloadPath?: string;
  externalRunId?: string;
  workspaceName?: string;
  pipelineName?: string;
  triggerName?: string;
  runGroupId?: string;
  invokedBy?: string;
  lastCheckedAt: string;
}

export interface IntegrationEvent {
  id: string;
  jobId?: string;
  eventType: string;
  sourceType: SourceType;
  sourceName: string;
  status: IntegrationStatus;
  severity: Severity;
  message: string;
  rawPayloadPath?: string;
  occurredAt: string;
}

export interface RerunJobResult {
  jobId: string;
  jobName: string;
  sourceType: SourceType;
  containerAppJobName: string;
  requestedAt: string;
  status: "REQUESTED";
  eventId: string;
  rawPayloadPath?: string;
  response?: unknown;
}

export interface SynapseTriggerRunRaw {
  triggerName?: string;
  triggerRunId?: string;
  triggerRunTimestamp?: string;
  status?: string;
  message?: string;
  properties?: Record<string, unknown>;
  [key: string]: unknown;
}

export interface ContainerAppJobExecutionRaw {
  id?: string;
  name?: string;
  resourceGroup?: string;
  type?: string;
  properties?: {
    startTime?: string;
    endTime?: string;
    status?: string;
    message?: string;
    template?: {
      containers?: Array<{
        name?: string;
        image?: string;
        imageType?: string;
        resources?: Record<string, unknown>;
        env?: Array<Record<string, unknown>>;
        [key: string]: unknown;
      }>;
      initContainers?: Array<Record<string, unknown>>;
      [key: string]: unknown;
    };
    [key: string]: unknown;
  };
  [key: string]: unknown;
}

export interface DashboardSummary {
  totalJobs: number;
  healthyJobs: number;
  runningJobs: number;
  failedJobs: number;
  missingJobs: number;
  lateJobs: number;
  longRunningJobs: number;
  cancelledJobs: number;
  unknownJobs: number;
  lastUpdatedAt: string;
}

export interface HealthMapNode {
  id: string;
  label: string;
  kind: "system" | "workspace" | "pipeline" | "task" | "api" | "database" | "storage" | "dashboard" | "alert";
  status: IntegrationStatus;
  severity: Severity;
  sourceType?: SourceType | "PLATFORM";
  x: number;
  y: number;
  owner?: string;
  subtitle?: string;
  detail: string;
  relatedJobId?: string;
}

export interface HealthMapEdge {
  id: string;
  source: string;
  target: string;
  label: string;
  status: IntegrationStatus;
  relationshipType?: "runs" | "writes_raw_payload" | "serving_reads_writes" | "queries" | "alert" | "depends_on";
}

export interface HealthMapNodePosition {
  nodeId: string;
  x: number;
  y: number;
}

export interface HealthMapLayout {
  version: number;
  positions: Record<string, HealthMapNodePosition>;
  updatedAt: string;
}

export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  message?: string;
  error?: {
    code: string;
    message: string;
  };
  meta?: {
    dataMode?: "mock" | "sql";
  };
  timestamp: string;
}
